import React from 'react';
import { Dialog, DialogContent, DialogActions, Avatar, Typography, Box, Button, Snackbar, Alert, Stack, Chip, TextField, IconButton } from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { getAvatarUrl } from '../../utils/avatar';
import { FriendService } from './FriendService';
import RankChip from '../rank/RankChip';
import CloseIcon from '@mui/icons-material/Close';
import PersonAddAlt1Icon from '@mui/icons-material/PersonAddAlt1';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';

export default function ProfileDialog({ open, onClose, user, currentUser }) {
  const { t } = useTranslation();
  const theme = useTheme();
  const [sent, setSent] = React.useState(false);
  const [snack, setSnack] = React.useState(null);
  const [message, setMessage] = React.useState('');
  const [sending, setSending] = React.useState(false);

  // Resolve current user id early so effects can use it safely
  const meId = String(currentUser?.id || currentUser?._id || 'me');
  const userId = String(user?.id || '');

  React.useEffect(() => {
    if (!open) { setSent(false); setSnack(null); setMessage(''); setSending(false); }
  }, [open]);

  // If an unfriend occurs, allow resending immediately
  React.useEffect(() => {
    const onFriendsChanged = () => {
      if (!FriendService.hasPendingOrFriend(meId, userId)) {
        setSent(false);
      }
    };
    try { window.addEventListener('friends:changed', onFriendsChanged); } catch {}
    return () => { try { window.removeEventListener('friends:changed', onFriendsChanged); } catch {} };
  }, [meId, userId]);

  const [canSend, setCanSend] = React.useState(true);
  React.useEffect(() => {
    // Recompute availability when dialog opens or friends state changes
    const recompute = async () => {
      const id = userId;
      if (!id) { setCanSend(true); return; }
      const API_BASE = (process.env.REACT_APP_API_BASE_URL || '/api').replace(/\/+$/, '');
      const token = (typeof localStorage !== 'undefined' ? localStorage.getItem('token') : null) || (typeof sessionStorage !== 'undefined' ? sessionStorage.getItem('token') : null);
      if (token) {
        try {
          const res = await fetch(`${API_BASE}/friends/status/${id}`, { headers: { 'Authorization': `Bearer ${token}` } });
          if (res.ok) {
            const data = await res.json();
            const allow = !(data?.friend || data?.pending);
            setCanSend(allow);
            if (allow) setSent(false);
            return;
          }
        } catch (_) {
          // Fall through to mock
        }
      }
      // Fallback to client-side mock service
      setCanSend(!FriendService.hasPendingOrFriend(meId, id));
    };
    recompute();
    const onChanged = () => { try { recompute(); } catch {} };
    try { window.addEventListener('friends:changed', onChanged); } catch {}
    return () => { try { window.removeEventListener('friends:changed', onChanged); } catch {} };
  }, [open, meId, userId]);

  const disabled = sent || !canSend || sending;

  const handleSend = async () => {
    setSending(true);
    // Prefer backend API if available (authenticated), else fallback to mock FriendService
    const API_BASE = (process.env.REACT_APP_API_BASE_URL || '/api').replace(/\/+$/, '');
    const token = (typeof localStorage !== 'undefined' ? localStorage.getItem('token') : null) || (typeof sessionStorage !== 'undefined' ? sessionStorage.getItem('token') : null);

    if (token) {
      try {
        const res = await fetch(`${API_BASE}/friends/requests`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({ toUserId: userId, message })
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) {
          // If exists or already friends, show info; otherwise fall back to mock
          if (data?.error && /exists|Đã là bạn bè|Yêu cầu đang chờ/i.test(String(data.error))) {
            setSnack({ type: 'info', msg: t('network.profile.toastExists', 'Request already sent or you are already friends.') });
            setSent(true);
            try { window.dispatchEvent(new Event('friends:changed')); } catch {}
            setSending(false);
            return;
          }
          // Fallback to mock service
          const local = FriendService.sendRequest(meId, userId);
          if (local.ok) {
            setSent(true);
            setSnack({ type: 'success', msg: t('network.profile.toastSent', 'Friend request sent to {{name}}!', { name: user.name }) });
            try { window.dispatchEvent(new Event('friends:changed')); } catch {}
            setSending(false);
            return;
          }
          if (local.error === 'exists') {
            setSnack({ type: 'info', msg: t('network.profile.toastExists', 'Request already sent or you are already friends.') });
            setSent(true);
            try { window.dispatchEvent(new Event('friends:changed')); } catch {}
            setSending(false);
            return;
          }
          setSnack({ type: 'error', msg: t('network.profile.toastFailed', 'Could not send request.') });
          setSending(false);
          return;
        }
        // Backend success
        setSent(true);
        setSnack({ type: 'success', msg: t('network.profile.toastSent', 'Friend request sent to {{name}}!', { name: user.name }) });
        try { window.dispatchEvent(new Event('friends:changed')); } catch {}
        setSending(false);
        return;
      } catch (_) {
        // Network error -> fallback
        const local = FriendService.sendRequest(meId, userId);
        if (local.ok) {
          setSent(true);
          setSnack({ type: 'success', msg: t('network.profile.toastSent', 'Friend request sent to {{name}}!', { name: user.name }) });
          try { window.dispatchEvent(new Event('friends:changed')); } catch {}
        } else if (local.error === 'exists') {
          setSnack({ type: 'info', msg: t('network.profile.toastExists', 'Request already sent or you are already friends.') });
          setSent(true);
          try { window.dispatchEvent(new Event('friends:changed')); } catch {}
        } else {
          setSnack({ type: 'error', msg: t('network.profile.toastFailed', 'Could not send request.') });
        }
        setSending(false);
        return;
      }
    }

    // No token: use client-side mock
    const res = FriendService.sendRequest(meId, userId);
    if (res.ok) {
      setSent(true);
      setSnack({ type: 'success', msg: t('network.profile.toastSent', 'Friend request sent to {{name}}!', { name: user?.name }) });
      try { window.dispatchEvent(new Event('friends:changed')); } catch {}
    } else if (res.error === 'exists') {
      setSnack({ type: 'info', msg: t('network.profile.toastExists', 'Request already sent or you are already friends.') });
      setSent(true);
      try { window.dispatchEvent(new Event('friends:changed')); } catch {}
    } else {
      setSnack({ type: 'error', msg: t('network.profile.toastFailed', 'Could not send request.') });
    }
    setSending(false);
  };

  // After hooks, it's safe to bail out early on missing user
  if (!user) return null;

  const displayName = user.name || 'User';
  const initials = displayName.slice(0, 1).toUpperCase();
  const subtitleParts = [user.major, user.school].filter(Boolean).join(' · ');

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        fullWidth
        maxWidth="xs"
        PaperProps={{
          sx: {
            borderRadius: 4,
            overflow: 'hidden',
            boxShadow: theme.palette.mode === 'dark'
              ? '0 20px 60px rgba(0,0,0,0.6)'
              : '0 20px 60px rgba(0,0,0,0.12)',
          }
        }}
      >
        {/* ── Gradient header with avatar ── */}
        <Box sx={{
          position: 'relative',
          background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, #7c3aed 50%, #ec4899 100%)`,
          pt: 4, pb: 6,
          textAlign: 'center',
          overflow: 'hidden',
        }}>
          {/* Decorative circles */}
          <Box sx={{ position: 'absolute', top: -30, right: -30, width: 120, height: 120, borderRadius: '50%', background: 'rgba(255,255,255,0.08)' }} />
          <Box sx={{ position: 'absolute', bottom: -20, left: -20, width: 80, height: 80, borderRadius: '50%', background: 'rgba(255,255,255,0.06)' }} />
          <Box sx={{ position: 'absolute', top: 10, left: '30%', width: 50, height: 50, borderRadius: '50%', background: 'rgba(255,255,255,0.04)' }} />

          {/* Close button */}
          <IconButton
            onClick={onClose}
            sx={{
              position: 'absolute', top: 8, right: 8,
              color: 'rgba(255,255,255,0.7)',
              '&:hover': { color: 'white', background: 'rgba(255,255,255,0.15)' },
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>

        {/* ── Avatar overlapping header ── */}
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mt: -5, position: 'relative', zIndex: 2 }}>
          <Box sx={{
            p: 0.5,
            borderRadius: '50%',
            background: theme.palette.background.paper,
            boxShadow: `0 4px 20px ${alpha(theme.palette.primary.main, 0.25)}`,
          }}>
            <Avatar
              src={getAvatarUrl(user)}
              sx={{
                width: 80, height: 80,
                fontSize: '2rem',
                fontWeight: 700,
                border: `3px solid ${theme.palette.background.paper}`,
                background: `linear-gradient(135deg, ${theme.palette.primary.main}, #7c3aed)`,
              }}
            >
              {initials}
            </Avatar>
          </Box>

          {/* Name + rank + subtitle */}
          <Typography variant="h6" fontWeight={800} sx={{ mt: 1.5, px: 2, textAlign: 'center' }}>
            {displayName}
          </Typography>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.5 }}>
            <RankChip userId={user.id} />
          </Stack>
          {subtitleParts && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, px: 2 }}>
              {subtitleParts}
            </Typography>
          )}
        </Box>

        {/* ── Content ── */}
        <DialogContent sx={{ px: { xs: 2.5, sm: 3 }, pt: 2.5, pb: 1 }}>
          {/* Bio */}
          <Box sx={{
            p: 2, borderRadius: 3, mb: 2.5,
            background: alpha(theme.palette.primary.main, 0.04),
            border: `1px solid ${alpha(theme.palette.primary.main, 0.08)}`,
          }}>
            <Typography variant="body2" sx={{ color: 'text.secondary', whiteSpace: 'pre-wrap', fontStyle: user.bio ? 'normal' : 'italic', textAlign: 'center' }}>
              {user.bio || t('network.profile.noBio', 'No bio yet.')}
            </Typography>
          </Box>

          {/* Message field */}
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
            <ChatBubbleOutlineIcon sx={{ fontSize: 18, color: theme.palette.primary.main }} />
            <Typography variant="caption" fontWeight={600} color="text.secondary">
              {t('network.profile.messageLabel', 'Add a message (optional)')}
            </Typography>
          </Stack>
          <TextField
            placeholder={t('network.profile.messagePlaceholder', "Hi! Let's connect and study together.")}
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            multiline
            minRows={2}
            fullWidth
            inputProps={{ maxLength: 500 }}
            disabled={disabled}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 3,
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: theme.palette.primary.main,
                },
              },
            }}
          />
          <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block', textAlign: 'right' }}>
            {message.length}/500
          </Typography>
        </DialogContent>

        {/* ── Actions ── */}
        <DialogActions sx={{ px: { xs: 2.5, sm: 3 }, pb: 2.5, pt: 1, flexDirection: 'column', gap: 1 }}>
          {sent ? (
            <Button
              fullWidth
              variant="contained"
              disabled
              startIcon={<CheckCircleOutlineIcon />}
              sx={{
                borderRadius: 3,
                textTransform: 'none',
                fontWeight: 700,
                py: 1.2,
                fontSize: '0.95rem',
                background: 'linear-gradient(135deg, #10b981, #059669)',
                color: 'white',
                '&.Mui-disabled': {
                  background: 'linear-gradient(135deg, #10b981, #059669)',
                  color: 'rgba(255,255,255,0.85)',
                },
              }}
            >
              {t('network.profile.sent', 'Request Sent')}
            </Button>
          ) : (
            <Button
              fullWidth
              variant="contained"
              onClick={handleSend}
              disabled={disabled}
              startIcon={<PersonAddAlt1Icon />}
              sx={{
                borderRadius: 3,
                textTransform: 'none',
                fontWeight: 700,
                py: 1.2,
                fontSize: '0.95rem',
                background: `linear-gradient(135deg, ${theme.palette.primary.main}, #7c3aed)`,
                boxShadow: `0 4px 14px ${alpha(theme.palette.primary.main, 0.3)}`,
                '&:hover': {
                  background: `linear-gradient(135deg, ${theme.palette.primary.dark}, #6d28d9)`,
                  transform: 'translateY(-1px)',
                  boxShadow: `0 6px 18px ${alpha(theme.palette.primary.main, 0.4)}`,
                },
                transition: 'all 0.2s',
              }}
            >
              {sending ? '...' : t('network.profile.sendFriendRequest', 'Send Friend Request')}
            </Button>
          )}
          <Button
            fullWidth
            onClick={onClose}
            sx={{
              borderRadius: 3,
              textTransform: 'none',
              fontWeight: 600,
              color: 'text.secondary',
              '&:hover': { background: alpha(theme.palette.action.hover, 0.08) },
            }}
          >
            {t('common.close', 'Close')}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={!!snack}
        autoHideDuration={3000}
        onClose={() => setSnack(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        {snack ? <Alert severity={snack.type} variant="filled" sx={{ width: '100%', borderRadius: 2 }}>{snack.msg}</Alert> : null}
      </Snackbar>
    </>
  );
}
